/**
 * Shopify globals shim
 * Creates window.Shopify object that CDN scripts expect
 *
 * IMPORTANT: Does NOT shim customerPrivacy — the real Shopify consent-tracking-api.js
 * sets window.Shopify.customerPrivacy. We use Object.defineProperty to intercept it
 * and auto-inject headless params (matching Hydrogen's approach).
 */

import { installCartApiShim } from './cart-api';

export interface ShimOptions {
  shopDomain: string;
  currency?: string;
  locale?: string;
  country?: string;
  storefrontRootDomain?: string;
  checkoutRootDomain?: string;
  storefrontAccessToken?: string;
}

/**
 * Feature definition for Shopify.loadFeatures()
 */
export interface ShopifyFeature {
  name: string;
  version: string;
}

export interface ShopifyGlobal {
  shop: string;
  currency: {
    active: string;
    rate: string;
  };
  country: string;
  locale: string;
  routes: {
    root: string;
  };
  analytics: {
    publish: (event: string, data: any) => void;
  };
  customerPrivacy?: any;
  /**
   * Load Shopify platform features (e.g., consent-tracking-api)
   * Used by consent management code
   */
  loadFeatures: (features: ShopifyFeature[], callback: (error?: Error) => void) => void;
}

declare global {
  interface Window {
    Shopify?: ShopifyGlobal;
  }
}

/**
 * Install all Shopify shims
 */
export function installAllShims(options: ShimOptions): void {
  if (typeof window === 'undefined') return;

  // Create window.Shopify if it doesn't exist
  if (!window.Shopify) {
    window.Shopify = {
      shop: options.shopDomain,
      currency: {
        active: options.currency || 'USD',
        rate: '1.0',
      },
      country: options.country || 'US',
      locale: options.locale || 'en',
      routes: {
        root: '/',
      },
      analytics: {
        publish: (event: string, data: any) => {
          window.dispatchEvent(
            new CustomEvent('shopify:analytics', {
              detail: { event, data },
            })
          );
        },
      },
      /**
       * Load Shopify platform features
       * Waits for the real consent-tracking-api.js to load before calling back.
       */
      loadFeatures: (features: ShopifyFeature[], callback: (error?: Error) => void) => {
        const needsConsentApi = features.some(
          (f) => f.name === 'consent-tracking-api'
        );

        if (!needsConsentApi) {
          // Non-consent features: callback immediately
          setTimeout(() => callback(), 0);
          return;
        }

        // If the real API is already loaded, callback immediately
        if (window.Shopify?.customerPrivacy) {
          setTimeout(() => callback(), 0);
          return;
        }

        // Wait for the real consent-tracking-api.js to set customerPrivacy
        const onLoaded = () => {
          clearTimeout(timeout);
          callback();
        };
        const timeout = setTimeout(() => {
          document.removeEventListener('consentTrackingApiLoaded', onLoaded);
          // Timeout fallback — callback without error so CDN scripts continue
          callback();
        }, 5000);
        document.addEventListener('consentTrackingApiLoaded', onLoaded, { once: true });
      },
    };
  }

  // Install customerPrivacy property watcher (Hydrogen pattern)
  // When the real consent-tracking-api.js sets customerPrivacy, intercept it
  // and wrap setTrackingConsent to auto-inject headless storefront params.
  installCustomerPrivacyWatcher(options);

  // Install cart API shim
  installCartApiShim({ currency: options.currency });
}

/**
 * Install a property watcher on window.Shopify.customerPrivacy
 *
 * When the real consent-tracking-api.js CDN script sets `customerPrivacy`,
 * we intercept it via Object.defineProperty and wrap `setTrackingConsent`
 * to auto-inject headless storefront params (domain, token).
 * This matches Hydrogen's approach.
 */
function installCustomerPrivacyWatcher(options: ShimOptions): void {
  if (!window.Shopify) return;

  let _customerPrivacy: any = undefined;

  Object.defineProperty(window.Shopify, 'customerPrivacy', {
    get() {
      return _customerPrivacy;
    },
    set(value) {
      _customerPrivacy = value;

      // When the real API is set, wrap setTrackingConsent with headless params
      if (value && typeof value.setTrackingConsent === 'function') {
        const original = value.setTrackingConsent;
        // Dot-prefix storefrontRootDomain for cross-subdomain cookies (Hydrogen pattern)
        const dotPrefixedDomain = options.storefrontRootDomain &&
          !options.storefrontRootDomain.startsWith('.')
            ? '.' + options.storefrontRootDomain
            : options.storefrontRootDomain;
        value.setTrackingConsent = function (consent: any, callback?: any) {
          return original.call(this, {
            ...consent,
            headlessStorefront: true,
            checkoutRootDomain:
              options.checkoutRootDomain || options.storefrontRootDomain,
            storefrontRootDomain: dotPrefixedDomain,
            storefrontAccessToken: options.storefrontAccessToken,
          }, callback);
        };
      }

      // Notify listeners that the real API is available
      document.dispatchEvent(new CustomEvent('consentTrackingApiLoaded'));
    },
    configurable: true,
    enumerable: true,
  });
}

/**
 * Update Shopify shim values
 */
export function updateShopifyShim(updates: Partial<ShimOptions>): void {
  if (typeof window === 'undefined' || !window.Shopify) return;

  if (updates.currency) {
    window.Shopify.currency.active = updates.currency;
  }
  if (updates.locale) {
    window.Shopify.locale = updates.locale;
  }
  if (updates.country) {
    window.Shopify.country = updates.country;
  }
}
